// useBaseSwitcher.js

import { useCallback, useMemo, useState } from 'react';

// Context
import { useBaseRoster } from 'context/BaseRosterContext';
import { useDatabase } from 'context/DatabaseContext';
import { useNavigation } from 'context/NavigationContext';
import { useStudio } from 'context/StudioContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useLocation, useNavigate } from 'react-router-dom';

// Utilities
import { SectionType } from 'utilities/Enums';
import { getSectionPath } from 'utilities/Routing';

// Services
import { createSetting, updateSetting } from 'services/settings/SettingWriteService';
import { getBaseKey } from 'services/bases/BaseReadService';
import { buildActiveBaseSettingPatch, getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';

/**
 * Shared base switcher state and handlers for the rail and sidebar switcher.
 */
export default function useBaseSwitcher() {
  const { setCurrentSection } = useNavigation();
  const { closeStudio } = useStudio();
  const { setActiveDatabase } = useDatabase();
  const {
    user,
    userSettings,
    setUserSettings,
  } = useUser();
  const { activeBase, setActiveBase } = useBase();
  const { bases: rosterBases } = useBaseRoster();

  const navigate = useNavigate();
  const location = useLocation();

  const [addOpen, setAddOpen] = useState(false);

  const bases = useMemo(() => {
    return Array.isArray(rosterBases) ? rosterBases : [];
  }, [rosterBases]);

  const activeBaseKey = useMemo(() => {
    const key = activeBase?.key || getActiveBaseKeyFromSettings(userSettings) || '';
    return String(key).trim();
  }, [activeBase?.key, userSettings]);

  const handleBaseSelect = useCallback(async (base) => {
    if (!user?.key) return;

    const baseKey = getBaseKey(base);
    if (!baseKey) return;

    const normalizedBase = {
      ...(base || {}),
      key: baseKey,
    };

    const homePath = getSectionPath(SectionType.HOME);
    const currentBaseKey = activeBase?.key || getActiveBaseKeyFromSettings(userSettings) || null;
    const isSwitchingBase = Boolean(baseKey && baseKey !== currentBaseKey);

    closeStudio();

    setUserSettings && setUserSettings({
      ...(userSettings || {}),
      ...buildActiveBaseSettingPatch(baseKey),
      userKey: user.key,
    });
    setActiveBase && setActiveBase(normalizedBase);

    if (isSwitchingBase) {
      setActiveDatabase && setActiveDatabase(null);
    }

    setCurrentSection && setCurrentSection(SectionType.HOME);

    try {
      try {
        if (userSettings?.id || userSettings?.userKey) {
          await updateSetting(user.key, buildActiveBaseSettingPatch(baseKey));
        } else {
          await createSetting(user.key, buildActiveBaseSettingPatch(baseKey));
        }
      } catch (error) {
        if (error?.code === 'not-found') {
          await createSetting(user.key, buildActiveBaseSettingPatch(baseKey));
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('[useBaseSwitcher] update ACTIVE_BASE_KEY error:', error);
    }

    if (location.pathname !== homePath) {
      navigate(homePath, { replace: true });
      return;
    }
  }, [
    activeBase?.key,
    closeStudio,
    location.pathname,
    navigate,
    setActiveDatabase,
    setActiveBase,
    setCurrentSection,
    setUserSettings,
    user?.key,
    userSettings,
  ]);

  const handleBaseAdd = useCallback(() => {
    setAddOpen(true);
  }, []);

  return {
    activeBaseKey,
    addOpen,
    handleBaseAdd,
    handleBaseSelect,
    setAddOpen,
    user,
    bases,
  };
}
